import { CalendarUnit, HiddenColumnGroupIndicator, NO_DATA_ROW_ID } from '@opengov/reporting-types';
import cx from 'classnames2';
import { cloneDeep } from 'lodash';
import React, { useMemo } from 'react';

import { ROW_HEIGHT } from '../constants';
import { cellFormatter, createAccountSummaryFormatter } from '../formatters';
import {
  CellFormatter,
  EColumnType,
  ETableContentType,
  ISummaryTable,
  ISummaryTableStyle,
  ITableRows,
  NAME_COL_ID,
  RowMeta,
  TableId,
  UnderlineRole
} from '../types';
import { getValueAlignment } from '../utils';
import { useTableKeyboardNav } from './useTableKeyboardNav';
export { scrollRowIntoContainerView, useTableKeyboardNav } from './useTableKeyboardNav';

import defaultTableStyle from './Table.scss';

const UNDERLINE_COLOR = '#5c5f66';

/**
 * Returns true when the group-level header row should be omitted.
 *
 * Datasource columns use {@link HiddenColumnGroupIndicator} when "Hide group-level headers"
 * is enabled. Calculation columns are skipped by that rewrite and keep a visible alias in this
 * row — matching reporting-app, which ignores calc columns when deciding whether to hide — so
 * any presence of the indicator means the entire group header row should be hidden.
 */
export function shouldHideColumnGroupHeaderRow(categoryCells: { value?: string }[]): boolean {
  const sanitizedValues = categoryCells.map((cell) =>
    (cell.value ?? '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim()
  );
  const allEmpty = sanitizedValues.every((value) => value === '');
  const hasHiddenIndicator = sanitizedValues.some((value) => value === HiddenColumnGroupIndicator);
  return allEmpty || hasHiddenIndicator;
}

/**
 * How many leading rows of the table content are column header rows.
 *
 * A column group organized by several levels emits one header row per level above the leaf, so the
 * header block is not a fixed two rows. Body rows never carry these two types, so the leading run of
 * them is the header block at any depth \u2014 and is still exactly two for a single-level report.
 *
 * Callers must derive the boundary through this rather than indexing rows 0 and 1: reading the leaf
 * row as `data[1]` picks an intermediate level instead, whose cells are colSpan-merged and far fewer
 * than the leaf columns, which silently truncates the table to that many columns.
 */
export function countHeaderRows(data: ISummaryTableRow[]): number {
  const headerTypes: ETableContentType[] = [ETableContentType.RowHeader, ETableContentType.RowSubHeader];
  let count = 0;
  for (const row of data) {
    if (!headerTypes.includes(row.type)) break;
    count++;
  }
  return count;
}

export interface ISummaryTableColumn {
  id: TableId;
  colSpan?: number;
  value?: string;
  format?: EColumnType;
  calendarUnit?: CalendarUnit;
}

export interface ISummaryTableRow {
  id: TableId;
  name?: string;
  type: ETableContentType;
  level?: number;
  col?: ISummaryTableColumn[];
  rows?: ISummaryTableRow[];
  format?: EColumnType;
}

export interface ISummaryTableRowStyles {
  rowGroupHeader?: 'rowGroupHeader';
  rowSubHeader?: 'rowSubHeader';
  rowGroupTotal?: 'rowGroupTotal';
  rowHeader?: 'rowHeader';
  row?: 'row';
  rowGroupTotalHeader?: 'rowGroupTotalHeader';
}

export interface ISummaryTableCellStyle {
  name?: string;
  value?: string;
}

export const tableStyle = {
  tableContainer: defaultTableStyle.tableContainer,
  tableContainerWithoutScroll: defaultTableStyle.tableContainerWithoutScroll,
  table: defaultTableStyle.table,
  row: {
    row: defaultTableStyle.row,
    rowGroupHeader: defaultTableStyle.rowGroupHeader,
    rowGroupTotal: defaultTableStyle.rowGroupTotal,
    rowGroupTotalHeader: defaultTableStyle.rowGroupTotalHeader,
    rowHeader: defaultTableStyle.rowHeader,
    rowSubHeader: defaultTableStyle.rowSubHeader
  },
  cell: {
    name: defaultTableStyle.name,
    value: defaultTableStyle.value
  },
  header: {
    name: defaultTableStyle.nameHeader,
    value: defaultTableStyle.valueHeader,
    colGroup: defaultTableStyle.colGroup
  }
} as ISummaryTableStyle;

/**
 * Max formatted-value length (`ch`) of the statementTotal row; used as underline span min-width.
 * Exported so chunked renderers (PDF export with `columnsPerPage`) can compute it once over
 * the full pre-split data and share it via `printStyle.underlineWidth`.
 */
export function computeUnderlineCh(
  bodyRows: ISummaryTableRow[],
  underlineRoleMap: Map<TableId, UnderlineRole> | undefined,
  formatter: CellFormatter
): number {
  if (!underlineRoleMap) return 0;
  const statementTotalId = [...underlineRoleMap.entries()].find(([_, role]) => role === 'statementTotal')?.[0];
  if (statementTotalId === undefined) return 0;
  const flat = flattenRows(bodyRows);
  const statementTotalRow = flat.find((r) => r.rowId === statementTotalId)?.row;
  if (!statementTotalRow?.col?.length) return 0;
  return Math.max(...statementTotalRow.col.map((c) => String(formatter(c.value ?? '', c.format, c.calendarUnit) ?? '').length));
}

/** Flatten nested rows in preorder (parent → children → next sibling). All rows visible, no expand/collapse. */
function flattenRows(data: ISummaryTableRow[], parentId: TableId | null = null): RowMeta[] {
  const visibleRows: RowMeta[] = [];
  function visit(row: ISummaryTableRow, pid: TableId | null) {
    visibleRows.push({
      row,
      rowId: row.id,
      level: row.level ?? 1,
      parentId: pid
    });
    if (row.rows?.length) {
      row.rows.forEach((child) => visit(child, row.id));
    }
  }
  data.forEach((r) => visit(r, parentId));
  return visibleRows;
}

type RawUnderlineRole = 'subtotal' | 'sectionTotal';

/** Assigns 'subtotal' to every RowGroupTotal in nested (non-root) groups, recursing into sub-groups. */
function collectNestedGroupRoles(rows: ISummaryTableRow[], roleMap: Map<TableId, RawUnderlineRole>): void {
  for (const row of rows) {
    if (row.type === ETableContentType.RowGroupTotal) {
      roleMap.set(row.id, 'subtotal');
    }
    if (row.type === ETableContentType.RowGroupHeader && row.rows?.length) {
      collectNestedGroupRoles(row.rows, roleMap);
    }
  }
}

/**
 * Assigns roles for a root-level group: the last RowGroupTotal becomes 'sectionTotal',
 * all others become 'subtotal'. Recurses into nested sub-groups via collectNestedGroupRoles.
 */
function collectRootGroupRoles(rows: ISummaryTableRow[], roleMap: Map<TableId, RawUnderlineRole>): void {
  const totalRows = rows.filter((r) => r.type === ETableContentType.RowGroupTotal);
  const lastTotalId = totalRows.at(-1)?.id ?? null;
  for (const row of rows) {
    if (row.type === ETableContentType.RowGroupTotal) {
      roleMap.set(row.id, row.id === lastTotalId ? 'sectionTotal' : 'subtotal');
    }
    if (row.type === ETableContentType.RowGroupHeader && row.rows?.length) {
      collectNestedGroupRoles(row.rows, roleMap);
    }
  }
}

function promoteLastSectionTotal(rawMap: Map<TableId, RawUnderlineRole>): Map<TableId, UnderlineRole> {
  const result = new Map<TableId, UnderlineRole>(rawMap as Map<TableId, UnderlineRole>);
  let lastSectionTotalId: TableId | null = null;
  for (const [id, role] of rawMap) {
    if (role === 'sectionTotal') lastSectionTotalId = id;
  }
  if (lastSectionTotalId !== null) {
    result.set(lastSectionTotalId, 'statementTotal');
  }
  return result;
}

export function buildUnderlineRoleMap(bodyRows: ISummaryTableRow[]): Map<TableId, UnderlineRole> {
  const rawMap = new Map<TableId, RawUnderlineRole>();
  for (const row of bodyRows) {
    if (row.type === ETableContentType.RowGroupHeader && row.rows?.length) {
      collectRootGroupRoles(row.rows, rawMap);
    }
    if (row.type === ETableContentType.RowGroupTotal) {
      rawMap.set(row.id, 'sectionTotal');
    }
  }
  return promoteLastSectionTotal(rawMap);
}

// Create Nested rows of the table from data Object and apply styles on them.
export const TableRows: React.FC<ITableRows> = ({
  data,
  style,
  maxColumn,
  stickyOffset = 0,
  parentBorders = {},
  isDetailsTable = false,
  formatter,
  printMode = false,
  printCellStyle = '',
  showBorders = { rows: true, cols: true },
  active = null,
  colIds = [],
  registerCellRef,
  onCellFocus,
  underlineRoleMap,
  underlineWidth
}) => {
  // Keyboard nav: only enable when parent provides ref registration and column ids
  const hasKeyboardNav = Boolean(registerCellRef && colIds.length > 0);
  let stickyRows = 0;
  const borders = cloneDeep(parentBorders);

  const getTotalRowStyle = (row: ISummaryTableRow) =>
    row.type === ETableContentType.RowGroupTotal && isDetailsTable && !isNaN(Number(row.name))
      ? defaultTableStyle.detailTableRowGroupTotalName
      : style.cell.name;

  const getTitle = (row: ISummaryTableRow) =>
    isDetailsTable && row.format ? cellFormatter(row.name, row.format) : row?.name;

  const getValueStyleClass = (row: ISummaryTableRow) => {
    if (isDetailsTable) return defaultTableStyle.detailTableValue;
    if (String(row.id) === NO_DATA_ROW_ID) return defaultTableStyle.noDataValue;
    return style.cell.value;
  };

  const getCellTabIndex = (rId: TableId, cId: TableId) =>
    hasKeyboardNav && active?.rowId === rId && active?.colId === cId ? 0 : -1;

  const getUnderlineCellStyle = (row: ISummaryTableRow): React.CSSProperties => {
    if (!underlineRoleMap) return {};
    const role = underlineRoleMap.get(row.id);
    if (!role) return {};
    const spanBase: React.CSSProperties = underlineWidth
      ? { display: 'inline-block', minWidth: `${underlineWidth}ch` }
      : { display: 'inline-block' };
    if (role === 'subtotal') {
      return { borderTop: `1px solid ${UNDERLINE_COLOR}`, borderBottom: `1px solid ${UNDERLINE_COLOR}`, ...spanBase };
    }
    if (role === 'sectionTotal' || role === 'statementTotal') {
      return { borderBottom: `4px double ${UNDERLINE_COLOR}`, ...spanBase };
    }
    return {};
  };

  const getNameCellBorderStyle = (row: ISummaryTableRow): React.CSSProperties => {
    if (!underlineRoleMap) return {};
    if (!underlineRoleMap.get(row.id)) return {};
    // When gridlines are on, keep the name cell's CSS-class border-bottom so
    // the gridline stays continuous across the row (visible at the table's
    // last total row). Only suppress it in underlines-only mode, where the
    // class border would otherwise paint a line under the label that
    // competes with the accounting underline on the value cells.
    if (showBorders.rows) return {};
    return { borderBottom: 'none', borderTop: 'none' };
  };

  const renderBodyCells = (row: ISummaryTableRow, rowId: TableId) => {
    if (!row.col.length) {
      return [...Array(maxColumn)].map((_, index) => {
        const colId = colIds[1 + index] ?? index;
        return (
          <td
            key={`${rowId}-${colId}`}
            className={cx(style.cell.value, printMode && printCellStyle)}
            data-border={showBorders.rows && borders[index]}
            ref={hasKeyboardNav ? (el) => registerCellRef?.(rowId, colId, el) : undefined}
            tabIndex={getCellTabIndex(rowId, colId)}
            onFocus={hasKeyboardNav ? () => onCellFocus?.(rowId, colId) : undefined}
          >
            &nbsp;
          </td>
        );
      });
    }
    return row.col.map((column, i) => {
      const colId = colIds[1 + i] ?? column?.id ?? i;
      const formattedValue = formatter(column?.value, column?.format, column?.calendarUnit);
      const underlineStyle = getUnderlineCellStyle(row);
      const hasUnderlineSpan = Object.keys(underlineStyle).length > 0;
      return (
        <td
          key={`${row.id}-${column?.id}`}
          colSpan={column?.colSpan ?? 1}
          className={cx(getValueStyleClass(row), printMode && printCellStyle)}
          data-border={showBorders.rows && borders[i]}
          style={getValueAlignment(column, isDetailsTable)}
          ref={hasKeyboardNav ? (el) => registerCellRef?.(rowId, colId, el) : undefined}
          tabIndex={getCellTabIndex(rowId, colId)}
          onFocus={hasKeyboardNav ? () => onCellFocus?.(rowId, colId) : undefined}
        >
          {hasUnderlineSpan ? <span style={underlineStyle}>{formattedValue}</span> : formattedValue}
        </td>
      );
    });
  };

  return (
    <>
      {data?.map((nestedRow, rowIndex) => {
        const cellStyle = { paddingLeft: nestedRow.level > 1 ? `${nestedRow.level * 10}px` : '10px' };
        const isStickyRow = ['stickyHeaderStyle', 'rowHeader', 'rowSubHeader'].includes(nestedRow.type);

        if (isStickyRow) stickyRows++;

        if (nestedRow.type === ETableContentType.RowHeader) {
          nestedRow.col.reduce((result, current) => {
            borders[result] = true;
            return result + (current.colSpan ?? 0);
          }, 0);
        }

        const rowId = nestedRow.id;
        const nameColId = colIds[0];
        const stickyOffsetPx = (stickyOffset + stickyRows - (isStickyRow ? 1 : 0)) * ROW_HEIGHT;
        // Only sticky header rows pin vertically. A pixel offset on ordinary rows would pin them to the
        // scroller top while scrolling, painting their opaque background over neighbouring row borders;
        // `auto` overrides the value inherited from the <tr> while `left: 0` still freezes the column.
        const nameCellStickyOffset = isStickyRow ? `${stickyOffsetPx}px` : 'auto';

        return (
          <React.Fragment key={nestedRow.id ?? `nested-${rowIndex}`}>
            <tr
              className={style.row[nestedRow.type]}
              style={{ ['--sticky-offset' as any]: `${(stickyOffset + stickyRows - 1) * ROW_HEIGHT}px` }}
            >
              <th
                scope="row"
                className={cx(getTotalRowStyle(nestedRow), {
                  [defaultTableStyle.eclipsOff]: printMode,
                  [printCellStyle]: printMode
                })}
                style={{
                  ...cellStyle,
                  ...getNameCellBorderStyle(nestedRow),
                  ['--sticky-offset' as any]: nameCellStickyOffset
                }}
                data-border={showBorders.rows}
                ref={hasKeyboardNav ? (el) => registerCellRef?.(rowId, nameColId, el) : undefined}
                tabIndex={getCellTabIndex(rowId, nameColId)}
                onFocus={hasKeyboardNav ? () => onCellFocus?.(rowId, nameColId) : undefined}
              >
                {getTitle(nestedRow)}
              </th>
              {renderBodyCells(nestedRow, rowId)}
            </tr>
            {nestedRow.rows && (
              <TableRows
                key={'nested-row'}
                parentBorders={borders}
                data={nestedRow.rows}
                style={style}
                maxColumn={maxColumn}
                stickyOffset={stickyOffset + stickyRows + (isStickyRow ? 1 : 0)}
                isDetailsTable={isDetailsTable}
                formatter={formatter}
                printMode={printMode}
                printCellStyle={printCellStyle}
                showBorders={showBorders}
                active={active}
                colIds={colIds}
                registerCellRef={registerCellRef}
                onCellFocus={onCellFocus}
                underlineRoleMap={underlineRoleMap}
                underlineWidth={underlineWidth}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export const MdsSummaryTable: React.FC<ISummaryTable> = ({
  columns,
  data,
  embedId,
  isDetailsTable = false,
  accountSummaryTableFormatting,
  printStyle
}) => {
  let stickyRows = 0;

  const areAllColumnGroupHeadersHidden = shouldHideColumnGroupHeaderRow(data[0].col ?? []);
  const headerRowCount = countHeaderRows(data);
  const headerRows = areAllColumnGroupHeadersHidden ? data.slice(1, headerRowCount) : data.slice(0, headerRowCount);
  const bodyRows = data.slice(headerRowCount);

  // Use chunk-local subHeader col count so empty-col group-header rows don't render extra
  // <td>s when `columnsPerPage` splits the table.
  const fallbackMaxColumn = columns?.length > 1 ? columns.length - 1 : 0;
  const maxColumn = headerRows.at(-1)?.col?.length ?? fallbackMaxColumn;
  const bodyFlattened = flattenRows(bodyRows);
  // Keyboard nav: flat list of visible rows (header + body) and column ids; NAME_COL_ID = empty first header cell
  const visibleRows: RowMeta[] = [
    ...headerRows.map((row) => ({ row, rowId: row.id, level: row.level ?? 1, parentId: null as TableId | null })),
    ...bodyFlattened
  ];
  // Cell ids come from the LEAF header row, the only one with a cell per column — the rows above it
  // are colSpan-merged, so keying off row 0 left the deeper columns without a navigable id.
  const colIds = useMemo(
    () => [NAME_COL_ID, ...(headerRows.at(-1)?.col ?? []).map((c) => c.id)],
    [data]
  );

  const { active, registerCellRef, onCellFocus, handleKeyDown } = useTableKeyboardNav(visibleRows, colIds);

  const borders = {};
  const stickyOffset = 0;
  const printCellStyle = printStyle?.fontSize ? defaultTableStyle[`cellStylePrintMode-${printStyle.fontSize}px`] : '';

  headerRows.forEach((row) => {
    if (row.type === ETableContentType.RowHeader) {
      row.col.reduce((result, current) => {
        borders[result] = true;
        return result + (current.colSpan ?? 0);
      }, 0);
    } else if (row.type === ETableContentType.RowSubHeader) {
      row.col.forEach((col) => {
        const columnLabel = col.value;
        if (columnLabel === null || columnLabel === undefined || columnLabel.toString() === 'null') {
          col.value = '';
        }
      });
    }
  });
  const showBorders = printStyle ? (printStyle?.showGridlines ?? false) : true;
  const formatter = createAccountSummaryFormatter(accountSummaryTableFormatting);
  const underlineRoleMap = printStyle?.showUnderlines ? buildUnderlineRoleMap(bodyRows) : undefined;
  const showHeaderUnderline = Boolean(printStyle?.showUnderlines);
  const headerUnderlineSpanStyle: React.CSSProperties = {
    display: 'inline-block',
    borderBottom: `1px solid ${UNDERLINE_COLOR}`
  };
  const headerValueCellStyle: React.CSSProperties | undefined = printStyle ? { textAlign: 'center' } : undefined;
  // Caller-supplied width (from full pre-split data) keeps all `columnsPerPage` chunks
  // aligned; fall back to chunk-local compute otherwise.
  const underlineWidth = printStyle?.underlineWidth ?? computeUnderlineCh(bodyRows, underlineRoleMap, formatter);

  const keepRowGroupsTogetherOnPageBreaks = printStyle?.keepRowGroupsTogetherOnPageBreaks ?? false;
  const keepColumnGroupsTogetherOnPageBreaks = printStyle?.keepColumnGroupsTogetherOnPageBreaks ?? false;

  const firstRootRowGroupIndex = bodyRows.findIndex((r) => r.type === ETableContentType.RowGroupHeader);
  const indexSkippingKeepTogetherPrint = Math.max(0, firstRootRowGroupIndex);

  const tableRowsSharedProps = {
    style: tableStyle,
    maxColumn,
    isDetailsTable,
    formatter,
    parentBorders: borders,
    printMode: !!printStyle,
    printCellStyle,
    showBorders: { rows: showBorders, cols: showBorders },
    active,
    colIds,
    registerCellRef,
    onCellFocus,
    underlineRoleMap,
    underlineWidth
  };

  const tableBody =
    keepRowGroupsTogetherOnPageBreaks && bodyRows.length > 0 ? (
      bodyRows.map((rootRow, index) => (
        <tbody
          key={`pdf-root-group-${String(rootRow.id)}-${index}`}
          className={cx(
            index !== indexSkippingKeepTogetherPrint && defaultTableStyle.keepRowGroupsTogetherTbody
          )}
        >
          <TableRows {...tableRowsSharedProps} data={[rootRow]} />
        </tbody>
      ))
    ) : (
      <tbody>
        <TableRows {...tableRowsSharedProps} data={bodyRows} />
      </tbody>
    );

  return (
    <div
      className={cx(tableStyle.tableContainer, printStyle && defaultTableStyle.tableContainerPrintMode)}
      data-report-table-container-id={embedId}
      data-table-scroll-container
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <table
        aria-describedby="table-mds-summary"
        className={cx(tableStyle.table, printStyle && defaultTableStyle[`tablePrintMode-${printStyle.fontSize}px`])}
        id="report-table"
        data-report-table-id={embedId}
        data-gridlines={printStyle && !showBorders ? 'false' : undefined}
        data-keep-row-groups={keepRowGroupsTogetherOnPageBreaks ? 'true' : undefined}
        data-keep-column-groups={keepColumnGroupsTogetherOnPageBreaks ? 'true' : undefined}
        style={printStyle?.showUnderlines && !printStyle?.showGridlines ? { borderSpacing: '8px 0' } : undefined}
      >
        <caption
          id="table-mds-summary"
          className={defaultTableStyle.tableCaption}
        >{`This table summarizes the data`}</caption>
        <thead>
          {headerRows.map((nestedRow, i) => {
            stickyRows++;
            const rowId = nestedRow.id;

            const stickyOffsetStyle = {
              ['--sticky-offset' as any]: printStyle ? '0px' : `${(stickyOffset + stickyRows - 1) * ROW_HEIGHT}px`
            };

            // Every row above the leaf is a group row, not just the first, so each gets the group
            // styling. For a single-level report this is still row 0 alone.
            const hasGroupedHeaders = i < headerRows.length - 1;

            return (
              <tr
                key={i}
                className={cx(tableStyle.row[nestedRow.type], printStyle && printCellStyle)}
                style={stickyOffsetStyle}
              >
                <th
                  scope="col"
                  className={cx(tableStyle.header.name, printStyle && printCellStyle)}
                  style={{
                    ...stickyOffsetStyle,
                    width: printStyle?.nameWidth && `${printStyle.nameWidth}px`,
                    textAlign: printStyle ? 'center' : undefined
                  }}
                  data-border={showBorders ? undefined : false}
                  ref={(el) => registerCellRef(rowId, colIds[0], el)}
                  tabIndex={active?.rowId === rowId && active?.colId === colIds[0] ? 0 : -1}
                  onFocus={() => onCellFocus(rowId, colIds[0])}
                >
                  <span className={defaultTableStyle.srOnly}>Row labels</span>
                </th>
                {nestedRow.col.map((col, j) => {
                  const colId = colIds[1 + j] ?? col.id;
                  const classes = [tableStyle.cell.value];
                  if (hasGroupedHeaders) {
                    classes.push(tableStyle.header.colGroup);
                  }
                  if (printStyle) {
                    classes.push(printCellStyle);
                  }
                  return (
                    <th
                      scope="col"
                      key={`${nestedRow.id}-${col.id}`}
                      className={classes.join(' ')}
                      colSpan={col.colSpan ?? 1}
                      style={headerValueCellStyle}
                      data-border={showBorders ? borders[j] : false}
                      ref={(el) => registerCellRef(rowId, colId, el)}
                      tabIndex={active?.rowId === rowId && active?.colId === colId ? 0 : -1}
                      onFocus={() => onCellFocus(rowId, colId)}
                    >
                      {showHeaderUnderline ? <span style={headerUnderlineSpanStyle}>{col.value}</span> : col.value}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        {tableBody}
      </table>
    </div>
  );
};

const HEADER_ROW_ID = '__header__';

export const SummaryTable: React.FC<ISummaryTable> = ({
  columns,
  data,
  style = tableStyle,
  embedId,
  isDetailsTable = false
}) => {

  const maxColumn = columns?.length > 1 ? columns.length - 1 : 0;
  const bodyRows = flattenRows(data ?? []);
  // Keyboard nav: flat visible rows (synthetic header + body) and column ids from columns array only
  const visibleRows: RowMeta[] = [
    { row: null, rowId: HEADER_ROW_ID, level: 1, parentId: null },
    ...bodyRows
  ];
  const colIds = useMemo(() => (columns ?? []).map((c) => c.id), [columns]);
  const { active, registerCellRef, onCellFocus, handleKeyDown } = useTableKeyboardNav(visibleRows, colIds);

  return (
    <div
      className={style.tableContainer}
      data-report-table-container-id={embedId}
      data-table-scroll-container
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <table
        aria-describedby="table-report-summary"
        className={style.table}
        id="report-table"
        data-report-table-id={embedId}
      >
        <caption id="table-report-summary" className={defaultTableStyle.tableCaption}>{`This table summarizes the data`}</caption>
        <thead>
          <tr className={style.row.row}>
            {columns?.map((item, index) => {
              const colId = colIds[index] ?? item.id;
              return (
                <th
                  scope="col"
                  key={`header-${item.id ?? index}`}
                  className={index === 0 ? style.header.name : style.header.value}
                  style={{ top: 0, ...(index === 0 ? {} : getValueAlignment(item, isDetailsTable)) }}
                  ref={(el) => registerCellRef(HEADER_ROW_ID, colId, el)}
                  tabIndex={active?.rowId === HEADER_ROW_ID && active?.colId === colId ? 0 : -1}
                  onFocus={() => onCellFocus(HEADER_ROW_ID, colId)}
                >
                  {item.value}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <TableRows
            data={data}
            style={style}
            maxColumn={maxColumn}
            isDetailsTable={isDetailsTable}
            formatter={cellFormatter}
            active={active}
            colIds={colIds}
            registerCellRef={registerCellRef}
            onCellFocus={onCellFocus}
          />
        </tbody>
      </table>
    </div>
  );
};
